/* eslint-disable no-classname/no-classname-outside-designsystem */
import { type AssetVizProps } from '@asset-viz/defines'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from 'designSystem/components/Button'
import { cn } from 'designSystem/lib/utils'
import { useGeckoLocale } from 'features/locale'
import { X } from 'lucide-react'
import * as React from 'react'
import {
  type GeckoColorScaleThreshold,
  type GeckoComponentDataFrame,
  type GeckoMeasurementRange,
} from 'types'

import { ForecastAssetViz } from './ForecastAssetViz'
import { ForecastAssetVizWindowSurface } from './forecastAssetVizWindow/ForecastAssetVizWindowSurface'
import { getCdfResolutionOptions } from './forecastAssetVizWindow/resolutionOptions'
import { useForecastAssetVizWindowState } from './forecastAssetVizWindow/useForecastAssetVizWindowState'

export interface ForecastAssetVizWindowProps extends AssetVizProps {
  className?: string
  children?: React.ReactNode
  selectedCDF?: GeckoComponentDataFrame
  resolutionDataFrames?: readonly GeckoComponentDataFrame[]
  onSelectedCDFChange?: (cdf: GeckoComponentDataFrame) => void
  initialShow3D?: boolean
  showViewModeToggle?: boolean
  showUnitsToggle?: boolean
  showResolutionSelector?: boolean
  showMeasurementStrip?: boolean
  showColorScaleSettings?: boolean
  expandable?: boolean
  expandedTitle?: React.ReactNode
  expandedDescription?: React.ReactNode
  topLeftContent?: React.ReactNode
  topCenterContent?: React.ReactNode
  selectedColorThreshold?: GeckoColorScaleThreshold
  onSelectedColorThresholdChange?: (
    threshold: GeckoColorScaleThreshold | undefined
  ) => void
  filteredValueRange?: GeckoMeasurementRange
  onFilteredValueRangeChange?: (
    range: GeckoMeasurementRange | undefined
  ) => void
}

export const ForecastAssetVizWindow = React.forwardRef<
  React.ElementRef<typeof ForecastAssetViz>,
  ForecastAssetVizWindowProps
>(function ForecastAssetVizWindow(
  {
    className,
    children,
    selectedCDF,
    resolutionDataFrames,
    onSelectedCDFChange,
    componentDataFrames,
    sceneConfig,
    onHovered,
    getColorThresholdForComponent,
    cdfToFilteredValueRange,
    onSceneConfigOptionsChanged,
    initialShow3D = true,
    showViewModeToggle = true,
    showUnitsToggle = false,
    showResolutionSelector = true,
    showMeasurementStrip = true,
    showColorScaleSettings = true,
    expandable = true,
    expandedTitle = 'Asset viewer',
    expandedDescription,
    topLeftContent,
    topCenterContent,
    selectedColorThreshold,
    onSelectedColorThresholdChange,
    filteredValueRange,
    onFilteredValueRangeChange,
    ...assetVizProps
  },
  ref
) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [internalSelectedCdfId, setInternalSelectedCdfId] =
    React.useState<string>()
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const providedSelectedCDF = selectedCDF ?? componentDataFrames?.[0]
  const resolutionOptions = React.useMemo(
    () =>
      getCdfResolutionOptions({
        cdfs: resolutionDataFrames ?? componentDataFrames ?? [],
        selectedCDF: providedSelectedCDF,
        getPreferredMeasurementUnit,
      }),
    [
      componentDataFrames,
      getPreferredMeasurementUnit,
      providedSelectedCDF,
      resolutionDataFrames,
    ]
  )
  const internalSelectedCDF = internalSelectedCdfId
    ? resolutionOptions.find(
        (option) => option.cdf.id === internalSelectedCdfId
      )?.cdf
    : undefined
  const effectiveSelectedCDF = internalSelectedCDF ?? providedSelectedCDF
  const effectiveComponentDataFrames = React.useMemo(
    () =>
      effectiveSelectedCDF
        ? [effectiveSelectedCDF]
        : (componentDataFrames ?? []),
    [componentDataFrames, effectiveSelectedCDF]
  )
  const selectedResolutionLabel =
    resolutionOptions.find(
      (resolutionOption) => resolutionOption.cdf.id === effectiveSelectedCDF?.id
    )?.label ?? 'Resolution'

  React.useEffect(() => {
    setInternalSelectedCdfId(undefined)
  }, [providedSelectedCDF?.id])

  const handleSelectResolution = React.useCallback(
    (cdfId: string) => {
      const nextCdf = resolutionOptions.find(
        (resolutionOption) => resolutionOption.cdf.id === cdfId
      )?.cdf
      if (!nextCdf) return

      if (onSelectedCDFChange) {
        onSelectedCDFChange(nextCdf)
        return
      }

      setInternalSelectedCdfId(nextCdf.id)
    },
    [onSelectedCDFChange, resolutionOptions]
  )
  const windowState = useForecastAssetVizWindowState({
    selectedCDF: effectiveSelectedCDF,
    initialShow3D,
    onHovered,
    getColorThresholdForComponent,
    cdfToFilteredValueRange,
    onSceneConfigOptionsChanged,
    selectedColorThreshold,
    onSelectedColorThresholdChange,
    filteredValueRange,
    onFilteredValueRangeChange,
  })

  return (
    <>
      {isExpanded ? (
        <div className={cn('relative overflow-hidden bg-black', className)} />
      ) : (
        <ForecastAssetVizWindowSurface
          ref={ref}
          {...assetVizProps}
          className={className}
          componentDataFrames={effectiveComponentDataFrames}
          sceneConfig={sceneConfig}
          windowState={windowState}
          showViewModeToggle={showViewModeToggle}
          showUnitsToggle={showUnitsToggle}
          showResolutionSelector={showResolutionSelector}
          resolutionOptions={resolutionOptions}
          selectedResolutionId={effectiveSelectedCDF?.id ?? ''}
          selectedResolutionLabel={selectedResolutionLabel}
          onSelectResolution={handleSelectResolution}
          showMeasurementStrip={showMeasurementStrip}
          showColorScaleSettings={showColorScaleSettings}
          topLeftContent={topLeftContent}
          topCenterContent={topCenterContent}
          onExpand={expandable ? () => setIsExpanded(true) : undefined}>
          {children}
        </ForecastAssetVizWindowSurface>
      )}

      {expandable ? (
        <Dialog.Root open={isExpanded} onOpenChange={setIsExpanded}>
          <Dialog.Portal>
            <Dialog.Overlay className='fixed inset-0 z-50 bg-black/55' />
            <Dialog.Content className='fixed left-1/2 top-1/2 z-50 flex h-[min(90vh,58rem)] w-[min(94rem,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg border bg-background text-foreground shadow-2xl focus:outline-none'>
              <header className='flex shrink-0 items-center justify-between border-b px-6 py-4'>
                <div className='min-w-0'>
                  <Dialog.Title className='truncate text-base font-semibold'>
                    {expandedTitle}
                  </Dialog.Title>
                  {expandedDescription ? (
                    <Dialog.Description className='mt-1 truncate text-xs text-muted-foreground'>
                      {expandedDescription}
                    </Dialog.Description>
                  ) : null}
                </div>
                <Dialog.Close asChild>
                  <Button variant='ghost' size='icon-sm' aria-label='Close'>
                    <X className='size-4' />
                  </Button>
                </Dialog.Close>
              </header>
              <div className='min-h-0 flex-1'>
                <ForecastAssetVizWindowSurface
                  ref={ref}
                  {...assetVizProps}
                  className='h-full min-h-0'
                  componentDataFrames={effectiveComponentDataFrames}
                  sceneConfig={sceneConfig}
                  windowState={windowState}
                  showViewModeToggle={showViewModeToggle}
                  showUnitsToggle={showUnitsToggle}
                  showResolutionSelector={showResolutionSelector}
                  resolutionOptions={resolutionOptions}
                  selectedResolutionId={effectiveSelectedCDF?.id ?? ''}
                  selectedResolutionLabel={selectedResolutionLabel}
                  onSelectResolution={handleSelectResolution}
                  showMeasurementStrip={showMeasurementStrip}
                  showColorScaleSettings={showColorScaleSettings}
                  topLeftContent={topLeftContent}
                  topCenterContent={topCenterContent}>
                  {children}
                </ForecastAssetVizWindowSurface>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      ) : null}
    </>
  )
})

ForecastAssetVizWindow.displayName = 'ForecastAssetVizWindow'
